// pages/EmbeddedChatAgents.jsx
// ----------------------------
// Main entry point for the embedded chat widget.
//
// This file is intentionally kept SHORT.  Its only jobs are:
//   1. Read URL search params and compute derived values (theme, colours, etc.)
//   2. Hold the UI-only state (is the chat open? which agent? what search term?)
//   3. Wire together the hook (session/messaging logic) and the components (UI).
//
// Where is everything else?
//   src/config/agents.js          → static agent config object
//   src/utils/chatHelpers.js      → getUserId, mapBackendMessagesToReachat
//   src/hooks/useChatSessions.js  → all backend API calls and session state
//   src/component/AgentSelector.jsx → agent-picker screen
//   src/component/ChatHeader.jsx    → header bar shown inside the chat

import React, { useEffect, useState, useRef } from 'react';
import axios from 'axios';
import './assets/App.css';
import './assets/idoks-new-theme.css';
import {
  Chat,
  ChatInput,
  SessionMessagePanel,
} from 'reachat';
import { FiMinus } from 'react-icons/fi';
import { chatTheme } from '../theme';
import { useSearchParams } from 'react-router-dom';
import agents from '../config/agents';
import { mapBackendMessagesToReachat } from '../utils/chatHelpers';
import useChatSessions from '../hooks/useChatSessions';
import AgentSelector from '../component/AgentSelector';
import ChatHeader from '../component/ChatHeader';
import CustomChatMessage from '../component/CustomChatMessage';

// Base URL for the Node.js KnowledgeGraph backend (fetches custom_agent config)
const KNOWLEDGEGRAPH_URL = process.env.REACT_APP_KNOWLEDGEGRAPH_NODE_BE_URL || 'http://localhost:3100';
// ChatAgent_Backend (Python FastAPI — port 8000). All agent API calls go here.
const API_BASE_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8000';
// DEV only: the Document Finder's whole flow as boxes and arrows (GET /idokschat/graph/map), fetched once per page
// load for the Answer Trail's full map. It only changes when the backend's code does.
let flowMapCache = null;

// How long an answer took: from the question before it to the answer (null for anything else). A live
// answer pairs two browser times; an answer from history pairs the two times the server saved.
const answerTookMs = (message, previous) => {
  if (!message || message.sender === 'user' || previous?.sender !== 'user') return null;
  const ms = Date.parse(message.receivedAt || message.timestamp) - Date.parse(previous.timestamp);
  return Number.isFinite(ms) && ms >= 0 ? ms : null;
};

const ChatAgent = ({ }) => {

  // --------------------------------------------------------------------------
  // 1. URL search params
  //    Everything the host page can configure is passed via query string.
  // --------------------------------------------------------------------------
  const [searchParams] = useSearchParams();

  const containerid = searchParams.get('containerid') || '';
  var visitorId = searchParams.get('visitorId') || null;
  visitorId = (visitorId === 'null') ? null : visitorId;

  var accessKey = searchParams.get('accessKey') || '';
  var userName = searchParams.get('userName') || '';
  var floatingWidget = searchParams.get('floatingWidget') || false;
  floatingWidget = (floatingWidget === 'true') ? true : (floatingWidget === 'false') ? false : floatingWidget;

  // ── Compact mode: botId param (custom_agent — all config from DB) ─────────
  // When botId is present the widget was embedded with the new short format:
  //   ReachatWidget.init({ botId: "my-bot-a3f2", accessKey, floatingWidget, containerid })
  // Everything else (colors, botName, windowSize, …) is fetched from the DB.
  //
  // ── Legacy mode: agentType + botId params ────────────────────────────────
  // Other agent types (knowledgebase_agent, idoks_agent, …) still pass the full
  // param set.  Reading them here keeps backward compatibility.
  const botIdParam = searchParams.get('botId') || '';

  // botId — for custom_agent this equals the botId slug; for others read param
  var botId = botIdParam || '';

  // botName / colors — used only for non-custom agents (legacy mode).
  // For custom_agent these come from DB config; defaults are just fallbacks.
  var botName = searchParams.get('botName') || '';
  var primarycolor = searchParams.get('primarycolor') || '';
  primarycolor = (primarycolor === 'null' || primarycolor === '') ? '#071739' : primarycolor;
  var secondarycolor = searchParams.get('secondarycolor') || '';
  secondarycolor = (secondarycolor === 'null' || secondarycolor === '') ? '#0059E1' : secondarycolor;

  // ── Host look-and-feel (opt-in, see public/chatwidget.js) ─────────────────
  // This widget is embedded in more than one product. It therefore must never
  // decide its own appearance from anything it can see — it wears what the host
  // asked for, and a host that asks for nothing keeps exactly the look it has
  // today. Only iDoks passes this, and only while its workspace is on the
  // redesign; the knowledge base and the custom bots pass nothing and so cannot
  // be reached by any of it.
  const hostTheme = searchParams.get('theme') || '';
  const isIdoksNewTheme = hostTheme === 'idoks-new';

  // The redesign's own tokens, the same navy iDoks uses (new-design.scss). The
  // defaults above stay exactly as they were for everybody else.
  const NEW_DESIGN_PRIMARY = '#194479';
  const NEW_DESIGN_SECONDARY = '#194479';

  // Applied ONLY where the host left the colour to the default. A host that
  // named a colour gets that colour, theme or no theme — being asked for a skin
  // is not a licence to overrule something stated outright.
  if (isIdoksNewTheme) {
    if (!searchParams.get('primarycolor') || searchParams.get('primarycolor') === 'null') {
      primarycolor = NEW_DESIGN_PRIMARY;
    }
    if (!searchParams.get('secondarycolor') || searchParams.get('secondarycolor') === 'null') {
      secondarycolor = NEW_DESIGN_SECONDARY;
    }
  }

  // sectionId — only used by idoks_agent / idokschat_agent (legacy mode)
  var sectionId = searchParams.get('sectionId') || '';

  // preview — set to true only when opened from the admin "Preview" button.
  // When true the Python backend includes the reasoning trace in every response
  // so the admin can see step-by-step tool calls inside the chat UI.
  // Production embeds never have this param, so reasoning is never exposed to end users.
  const isPreview = searchParams.get('preview') === 'true';

  // defaultbot — botId always means custom_agent; otherwise read agentType param
  var defaultbot = botIdParam ? 'custom_agent' : (searchParams.get('agentType') || null);
  defaultbot = (defaultbot === 'null') ? null : defaultbot;

  // --------------------------------------------------------------------------
  // 3. UI-only state (not related to backend data)
  // --------------------------------------------------------------------------
  const [isChatOpen, setIsChatOpen] = useState(floatingWidget === false ? true : false);
  const [showMenu, setShowMenu] = useState(false);
  const [agent, setAgent] = useState(defaultbot);
  const [pickedFiles, setPickedFiles] = useState([]);
  const [pickedFileIds, setPickedFileIds] = useState([]);
  const [pickedSections, setPickedSections] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [chatContext, setChatContext] = useState({});

  // ── custom_agent: config loaded from DB ────────────────────────────────────
  // The embed snippet only passes: agentKey + apikey + floatingWidget + containerid.
  // All appearance settings (botName, colors, windowSize, position, suggestions…)
  // are fetched from GET /chatagent/public/:agentKey and stored here.
  // All other agent types are completely unaffected by this state.
  const [customAgentConfig, setCustomAgentConfig] = useState(null);
  const [isAgentInactive, setIsAgentInactive] = useState(false);

  // User's IANA timezone — fetched from ChatAgent_Backend on mount using the
  // userkey URL param (which is the userId already present in every embed call).
  // Falls back to 'Asia/Kolkata' for anonymous users or when the backend is unreachable.
  const [userTimezone, setUserTimezone] = useState('Asia/Kolkata');

  // Effective theme values: DB config takes precedence for custom_agent;
  // for all other agents these equal the original URL param values (no change).
  const activePrimaryColor = (agent === 'custom_agent' && customAgentConfig?.widgetConfig?.primaryColor)
    ? customAgentConfig.widgetConfig.primaryColor
    : primarycolor;
  const activeSecondaryColor = (agent === 'custom_agent' && customAgentConfig?.widgetConfig?.secondaryColor)
    ? customAgentConfig.widgetConfig.secondaryColor
    : secondarycolor;
  const activeBotName = (agent === 'custom_agent' && customAgentConfig)
    ? (customAgentConfig.widgetConfig?.botName || customAgentConfig.name || botName)
    : botName;
  // Bubble image — shown on the floating button (null = use default SVG icon)
  const activeBubbleImage = (agent === 'custom_agent' && customAgentConfig?.widgetConfig?.bubbleImage)
    ? customAgentConfig.widgetConfig.bubbleImage : null;

  // Header image — shown in chat header avatar (null = show initials)
  const activeHeaderImage = (agent === 'custom_agent' && customAgentConfig?.widgetConfig?.headerImage)
    ? customAgentConfig.widgetConfig.headerImage : null;

  // Bubble button size in px (default 60)
  const activeBubbleSize = (agent === 'custom_agent' && customAgentConfig?.widgetConfig?.bubbleSize)
    ? customAgentConfig.widgetConfig.bubbleSize : 56;

  // Legacy fallback: old agents used single botImage for both
  const activeBotImage = activeHeaderImage;

  // Corner radius applied to the chat window (default 16px)
  const activeBorderRadius = (agent === 'custom_agent' && customAgentConfig?.widgetConfig?.borderRadius !== undefined)
    ? customAgentConfig.widgetConfig.borderRadius
    : 16;

  // Chat window size: compact | normal | large | fullscreen — all responsive
  const activeWindowSize = (agent === 'custom_agent' && customAgentConfig?.widgetConfig?.windowSize)
    ? customAgentConfig.widgetConfig.windowSize
    : 'normal';

  // Bubble and window position: 'bottom-right' | 'bottom-left'
  const activePosition = (agent === 'custom_agent' && customAgentConfig?.widgetConfig?.position)
    ? customAgentConfig.widgetConfig.position
    : 'bottom-right';

  // Bubble offset from edges (px)
  const activeBubbleOffsetBottom = (agent === 'custom_agent' && customAgentConfig?.widgetConfig?.bubbleOffsetBottom != null)
    ? customAgentConfig.widgetConfig.bubbleOffsetBottom : 20;
  const activeBubbleOffsetSide = (agent === 'custom_agent' && customAgentConfig?.widgetConfig?.bubbleOffsetSide != null)
    ? customAgentConfig.widgetConfig.bubbleOffsetSide : 20;

  // Show "Powered by iDoks" branding badge at the bottom of the chat
  const activeShowBranding = (agent === 'custom_agent' && customAgentConfig !== null)
    ? customAgentConfig.widgetConfig?.showBranding !== false
    : false;

  // Inline style for the floating chat window (custom_agent only)
  // All sizes use responsive CSS — they shrink on small screens automatically.
  const isFullscreen = activeWindowSize === 'fullscreen';
  const windowSizeDimensions = {
    compact:    { width: 'min(320px, 92vw)',  height: 'min(520px, 88vh)' },
    normal:     { width: 'min(400px, 94vw)',  height: 'min(640px, 88vh)' },
    large:      { width: 'min(520px, 96vw)',  height: 'min(80vh, 800px)' },
    fullscreen: { width: '100vw',             height: '100vh'             },
  };
  const chatWindowStyle = (agent === 'custom_agent' && floatingWidget !== false)
    ? {
        bottom: isFullscreen ? '0' : `${activeBubbleOffsetBottom}px`,
        ...(activePosition === 'bottom-right'
          ? { right: isFullscreen ? '0' : `${activeBubbleOffsetSide}px`, left: 'auto' }
          : { left: isFullscreen ? '0' : `${activeBubbleOffsetSide}px`, right: 'auto' }
        ),
        ...(windowSizeDimensions[activeWindowSize] || windowSizeDimensions.normal),
        borderRadius: isFullscreen ? '0' : `${activeBorderRadius}px`,
        maxHeight: isFullscreen ? '100vh' : 'calc(100vh - 32px)',
        maxWidth: isFullscreen ? '100vw' : '96vw',
      }
    : {};

  // --------------------------------------------------------------------------
  // 2. Theme
  //
  // WHY we DON'T use Tailwind dynamic classes like `bg-[${activePrimaryColor}]`:
  //   Tailwind JIT scans source files for class names at BUILD time.
  //   A class name that contains a JavaScript variable is never seen by the
  //   scanner, so the CSS rule is never generated — the color has no effect.
  //   Only the 5 colours in tailwind.config.js `safelist` would ever work.
  //
  // THE FIX — inject a real <style> block at runtime:
  //   We use a single stable CSS class name (`bot-primary-color`) and generate
  //   the background-color rule dynamically.  This works for ANY hex color —
  //   DB-configured, URL-param, or hardcoded — with zero build-time dependency.
  //
  // All existing components (ChatHeader, CustomChatMessage, RenderComponents)
  // receive `bottheme` as a className prop and keep working unchanged.
  // --------------------------------------------------------------------------
  const bottheme = (activeBotName === 'iKomet') ? 'bot-primary-ikomet' : 'bot-primary-color';
  // CSS injected into the page — see <style> tag in the render below
  const botColorCSS = [
    `.bot-primary-color { background-color: ${activePrimaryColor}; }`,
    `.bot-primary-ikomet { background-color: #fdbd2c; }`,
  ].join('\n');
  const parsedTheme = JSON.parse(chatTheme.replaceAll('{themeclass}', bottheme));

  const messagesContainerRef = useRef(null);
  const lastMsgRef = useRef(null);         // attached to the last message wrapper
  const prevMsgCountRef = useRef(0);       // tracks previous message count to detect new arrivals
  const chatInputRef = useRef(null);       // wrapper around ChatInput — used to disable textarea directly
  const bottomAnchorRef = useRef(null);    // invisible div at the very bottom — used for scrollIntoView

  // --------------------------------------------------------------------------
  // 4. Session / messaging logic (all API calls live in this hook)
  // --------------------------------------------------------------------------
  const {
    sessions,
    activeSessionId,
    currentSessionMessages,
    loading,
    isTyping,
    progress,
    sessionError,
    isCreatingSession,
    fetchAllSessions,
    fetchMessagesForSession,
    handleNewSession,
    handleSelectSession,
    handleSendMessage,
    handleDeleteSession,
    cancelRequest,
    hasRetry,
    retrySessionAction,
  } = useChatSessions({ agentType: agent, botId, pickedFileIds, chatContext, visitorId });

  // --------------------------------------------------------------------------
  // 5. UI event handlers
  // --------------------------------------------------------------------------

  // Asks the host page to close the big modals it opened for this chat (Answer Trail, and the
  // dev-only Agent Map). They belong to the conversation on screen, so they must not stay
  // open over the page after it is minimised or replaced by a new one.
  const closeHostModals = () => {
    window.parent.postMessage({ type: 'botMessage', containerid, action: 'closeModals' }, '*');
  };

  // Open / close the floating chat bubble
  const toggleChat = () => {
    closeHostModals();
    if (floatingWidget === false) {
      window.parent.postMessage({ type: 'botMessage', containerid, action: 'close' }, '*');
      return;
    }
    setIsChatOpen(!isChatOpen);

    // These land as inline width/height on the HOST's iframe container, so the units
    // resolve against the HOST page — which is the only way this iframe can reason about
    // a viewport it cannot measure (its own `innerWidth` is the 60px bubble when closed).
    //
    // The fixed 464px (`80px + 24rem`) is wider than a phone. Anchored bottom-right, it
    // hung ~90px off the left edge of a 375px screen, and `100vh` against a container
    // sitting 1.25rem above the bottom pushed the header off the top. Clamping to the
    // viewport is the whole fix. Only the redesign gets it; every other host keeps the
    // exact numbers it has always been sent.
    const openH = isIdoksNewTheme ? 'calc(100vh - 2.5rem)' : '100vh';
    const openW = isIdoksNewTheme ? 'min(calc(80px + 24rem), calc(100vw - 16px))' : 'calc(80px + 24rem)';

    let h = isChatOpen ? '60px' : openH;
    let w = isChatOpen ? '80px' : openW;
    window.parent.postMessage({ type: 'botMessage', containerid, action: 'resize', payload: { height: h, width: w } }, '*');
  };

  // Called when the user selects an agent from the AgentSelector grid
  const onSelectAgent = (chatagent) => {
    setAgent(chatagent);
  };

  // Going back means returning to whichever screen did the choosing. When the host
  // named the agent itself — iDoks does on the redesign — that screen is the host's,
  // so say so and let it decide; otherwise fall back to the widget's own picker.
  const onBackToSelector = () => {
    if (isIdoksNewTheme && defaultbot) {
      window.parent.postMessage({ type: 'botMessage', containerid, action: 'back' }, '*');
      return;
    }
    setAgent(null);
  };

  // Opens the full technical trace behind a bot answer in a big modal on the HOST page
  // (iDoks), not inside this narrow iframe — there is nowhere near enough room here for
  // a multi-step tool-call trace. `question` is looked up by the caller (the preceding
  // user turn in currentSessionMessages), since this component only has the bot message.
  const onViewFullTrace = async (message, question) => {
    const flow = message.flowSteps || [];
    // DEV only: the whole flow (every step and route, read from the backend's real graph) goes along, so the
    // Answer Trail draws the full map with this answer's path lit up. Fetched once; without it (production, or
    // the backend's dev switch off) the Answer Trail draws only the steps that ran, as before.
    let flowMap = null;
    if (process.env.NODE_ENV === 'development' && flow.length) {
      try {
        if (!flowMapCache) {
          const res = await fetch(`${API_BASE_URL}/idokschat/graph/map`);
          if (res.ok) flowMapCache = await res.json();
        }
        flowMap = flowMapCache;
      } catch (e) {
        flowMap = null;
      }
    }
    window.parent.postMessage({
      type: 'botMessage',
      containerid,
      action: 'openFullTrace',
      payload: {
        question: question || '',
        summary: message.howFoundSummary || '',
        trace: message.fullTrace || [],
        flow,
        ...(flowMap ? { flowMap } : {}),
      },
    }, '*');
  };

  // DEV only (the header button that calls this is not rendered in a production build).
  // Fetches how this agent is built and hands it to the host page to draw as a diagram in
  // its own big modal - same reason as the trace above: no room for it in this iframe.
  const onViewAgentMap = async () => {
    const send = (payload) => window.parent.postMessage(
      { type: 'botMessage', containerid, action: 'openAgentMap', payload }, '*');
    try {
      const params = new URLSearchParams({
        agentType: agent,
        botId: botId || '',
        workspaceId: chatContext?.workspaceId || '',
      });
      const res = await fetch(`${API_BASE_URL}/agents/blueprint?${params.toString()}`);
      if (!res.ok) {
        send({ error: res.status === 404
          ? 'The agent map is switched off on this server (ENABLE_AGENT_BLUEPRINT is not set).'
          : `Could not load the agent map (HTTP ${res.status}).` });
        return;
      }
      send({ blueprint: await res.json() });
    } catch (err) {
      send({ error: `Could not reach the chat backend: ${err.message}` });
    }
  };

  // DEV only (the header button that calls this is not rendered in a production build).
  // Saves every question and answer of the current conversation, each answer followed by
  // its full Answer Trail, as one Markdown file. Built here from what the widget already
  // holds, so nothing is fetched and the host page is not involved.
  const onExportChat = () => {
    const pad = (n) => String(n).padStart(2, '0');
    const now = new Date();
    const stamp = `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())} ${pad(now.getHours())}:${pad(now.getMinutes())}`;
    const fileStamp = stamp.replace(' ', '_').replace(':', '-');
    const agentName = activeBotName || agents[agent]?.name || agent || '';

    const messages = currentSessionMessages || [];
    const questions = messages.filter(m => m.sender === 'user').length;
    const lines = [
      '# Chat Export',
      '',
      `Agent: ${agentName}`,
      `Exported: ${stamp}`,
      `Messages: ${messages.length} (${questions} questions, ${messages.length - questions} answers)`,
      '',
      '---',
      '',
    ];

    // A bot message's content is an object ({ kind, payload }), not a string; the payload is
    // normally the text but cards (selection lists, errors) carry an object, so fall back to JSON.
    const toText = (c) => {
      if (c == null) return '';
      if (typeof c === 'string') return c;
      const p = c.payload !== undefined ? c.payload : c;
      if (typeof p === 'string') return p;
      try { return '```json\n' + JSON.stringify(p, null, 2) + '\n```'; } catch (e) { return String(p); }
    };

    let turn = 0;
    messages.forEach((m) => {
      if (m.sender === 'user') {
        turn += 1;
        lines.push(`## ${turn}. Question`, toText(m.content), '');
        return;
      }
      lines.push(turn ? `## ${turn}. Answer` : '## Greeting', toText(m.content), '');
      if (m.howFoundSummary || (m.fullTrace && m.fullTrace.length)) {
        lines.push('### Answer Trail', '');
        if (m.howFoundSummary) lines.push('**Summary**', m.howFoundSummary, '');
        if (m.fullTrace && m.fullTrace.length) {
          lines.push('**Step by step**');
          m.fullTrace.forEach((s, i) => {
            const body = String(s.content ?? '').split('\n').map(l => `   ${l}`).join('\n');
            lines.push(`${i + 1}. ${String(s.label || '').toUpperCase()}`, body);
          });
          lines.push('');
        }
      }
      lines.push('---', '');
    });

    const blob = new Blob([lines.join('\n')], { type: 'text/markdown;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `chat-export_${fileStamp}.md`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  };

  // Wraps handleDeleteSession so the menu also closes
  const onClearChat = async () => {
    closeHostModals();
    setShowMenu(false);
    await handleDeleteSession();
  };

  // Clears the section/directory filter from the payload
  const handleClear = () => {
    let payload = chatContext;
    if (payload?.currentPathname) {
      payload['currentPathname'] = '';
      setPickedSections('');
    }
    if (payload?.currentDirectory) {
      payload['currentDirectory'] = [];
    }
    setChatContext(payload);
  };

  // --------------------------------------------------------------------------
  // 6. Effects
  // --------------------------------------------------------------------------

  // Listen for messages from the host page (file selections, payload updates)
  useEffect(() => {
    // Commented-out WebSocket wiring left intentionally for future reference:
    // const ws = new WebSocket("wss://yourdomain/chat/SESSION_ID");
    // ws.onmessage = (e) => { const data = JSON.parse(e.data); if (data.event === "message") { displayChat(data.from, data.message); } };
    // ws.send(JSON.stringify({ event: "message", sender: "user", message: "I want to report a transaction dispute" }));

    const handleMessage = (e) => {
      if (e.data?.type === 'userMessage') {
        console.log('message receiving');
        if (e.data?.action === 'updatePayload') {
          setChatContext(e.data?.payload);
          if (e.data?.payload?.pickfiles) {
            if (e.data?.payload?.pickfiles) {
              setPickedFiles(e.data?.payload?.pickfiles);
              const pickfiles = e.data?.payload?.pickfiles || [];
              let fileIds = pickfiles.map(file => file._id);
              fileIds.sort();
              setPickedFileIds(fileIds);
            } else {
              setPickedFiles([]);
              setPickedFileIds([]);
            }
          } else if (agent === 'idokschat_agent') {
            if (e.data?.payload?.currentPathname) {
              setPickedSections(e.data?.payload?.currentPathname);
            } else {
              setPickedSections('');
            }
          }
        }
      }
    };

    // Set initial chatContext based on which agent is active.
    // `preview` is forwarded to the Python backend so it knows to include
    // reasoning traces — only true when opened from the admin preview URL.
    // chatContext content keys match the names that agent tools read
    // from the request_payload ContextVar (accessKey, agentType, botId).
    if (agent === 'idoks_agent') {
      setChatContext({ sectionId, botName, accessKey, userName, preview: isPreview });
    }
    if (agent === 'knowledgebase_agent') {
      setChatContext({ agentType: agent, botId, botName, accessKey, userName, preview: isPreview });
    }
    if (agent === 'custom_agent') {
      setChatContext({ agentType: agent, botId, accessKey, userName, preview: isPreview });
    }

    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, []);

  // Re-fetch sessions whenever the active agent or agent instance changes
  useEffect(() => {
    if (agent != null) {
      fetchAllSessions();
    }
  }, [agent, botId]);

  // Every time the user opens the chat (clicks bubble) OR a session becomes
  // available, re-fetch conversation from DB in silent mode (no loading spinner)
  // so messages are always current. If /chat is still in flight the typing
  // indicator appears automatically (fetchMessagesForSession detects last
  // message is from user and starts polling).
  useEffect(() => {
    if (isChatOpen && activeSessionId) {
      fetchMessagesForSession(activeSessionId, { silent: true });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isChatOpen, activeSessionId]);

  // Auto-scroll helpers
  // Uses scrollTo on the known scrollable container directly — more reliable than
  // scrollIntoView which may target the wrong ancestor (window instead of the div).
  const scrollToBottom = (smooth = true) => {
    setTimeout(() => {
      const c = messagesContainerRef.current;
      if (!c) return;
      c.scrollTo({ top: c.scrollHeight, behavior: smooth ? 'smooth' : 'auto' });
    }, 60);
  };

  // While an answer types itself out it grows below the visible area, so the person saw
  // nothing new until they scrolled. Follow the new text down - unless they have scrolled
  // up to read (stickToBottomRef turns false on their scroll and true again near the bottom).
  const stickToBottomRef = useRef(true);
  const keepNewestTextInView = () => {
    const c = messagesContainerRef.current;
    if (c && stickToBottomRef.current) c.scrollTop = c.scrollHeight;
  };

  // Returns true when the user is within 150px of the bottom — used to decide
  // whether to auto-scroll on bot messages without disrupting manual scrolling.
  const isNearBottom = () => {
    const c = messagesContainerRef.current;
    if (!c) return true;
    return c.scrollHeight - c.scrollTop - c.clientHeight < 150;
  };

  // Auto-scroll behaviour:
  //   • New user message          → always scroll to bottom (show typing indicator)
  //   • New bot message           → scroll if user was already near the bottom
  //   • isTyping starts           → scroll so typing indicator is visible
  //   • Initial load/session swap → snap to bottom instantly
  //   • Typewriter content update (same message count) → no scroll
  useEffect(() => {
    const msgCount = currentSessionMessages.length;
    const prevCount = prevMsgCountRef.current;

    if (msgCount > prevCount) {
      const isHistoryLoad = prevCount === 0 || (msgCount - prevCount) > 2;
      if (isHistoryLoad) {
        // Initial load or session switch — snap instantly, no animation
        scrollToBottom(false);
      } else {
        const lastMsg = currentSessionMessages[msgCount - 1];
        if (lastMsg?.sender === 'user') {
          // User just sent — always scroll so typing indicator comes into view
          scrollToBottom(true);
        } else {
          // Bot reply arrived — scroll if the user hasn't scrolled away to read history
          if (isNearBottom()) scrollToBottom(true);
        }
      }
      prevMsgCountRef.current = msgCount;
    } else if (isTyping && isNearBottom()) {
      // Typing indicator just appeared — scroll so it's visible
      scrollToBottom(true);
    }
    // Typewriter content updates (same message count, not typing) — no scroll
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentSessionMessages, isTyping, loading, isChatOpen]);

  // ── Disable / re-enable the ChatInput textarea while bot is typing ─────────
  // reachat's <ChatInput disabled={...}> does not wire the disabled prop to the
  // underlying <textarea> element, so we do it directly via a DOM ref.
  // pointer-events:none on the wrapper blocks mouse interaction; setting
  // textarea.disabled blocks keyboard input even if the element is Tab-focused.
  useEffect(() => {
    if (!chatInputRef.current) return;
    const ta = chatInputRef.current.querySelector('textarea');
    if (ta) ta.disabled = isTyping;
  }, [isTyping]);

  // ── Fetch user timezone on mount ──────────────────────────────────────────
  // For real users (dms / KB agents): looks up by userkey (MongoDB ObjectId).
  // For anonymous users (custom_agent public embeds): looks up by apikey instead
  // since the embed snippet's apikey maps to a real user in the KB users table.
  useEffect(() => {
    const uid = visitorId || '';
    // Always call — backend handles anonymous+apikey path (custom_agent case)
    const params = new URLSearchParams({ user_id: uid });
    if (accessKey) params.set('apikey', accessKey);
    axios.get(`${API_BASE_URL}/user/timezone?${params.toString()}`)
      .then(res => {
        const tz = res.data?.timeZone;
        if (tz) setUserTimezone(tz);
      })
      .catch(() => { /* keep default Asia/Kolkata on any error */ });
  }, [visitorId, accessKey]);

  // ── custom_agent only: fetch agent config from DB ─────────────────────────
  // Fetches botName, primaryColor, secondaryColor etc. so we don't need them
  // in the URL — keeping the embed snippet short and clean.
  // Other agents are never affected (guard: agent === 'custom_agent').
  useEffect(() => {
    if (agent === 'custom_agent' && botId) {
      axios.get(`${KNOWLEDGEGRAPH_URL}/chatagent/public/${botId}`)
        .then(res => {
          if (res.data?.status && res.data?.data) {
            if (res.data.data.status === 'Inactive') {
              setIsAgentInactive(true);
            } else {
              setCustomAgentConfig(res.data.data);
            }
          }
        })
        .catch(err => console.error('[custom_agent] Failed to load config:', err));
    }
  }, [agent, botId]);

  // The one place the skin is switched on. Every rule in idoks-new-theme.css is
  // scoped under this class, so with it absent the file cannot affect anything.
  useEffect(() => {
    const root = document.documentElement;
    if (isIdoksNewTheme) root.classList.add('idoks-new-theme');
    return () => root.classList.remove('idoks-new-theme');
  }, [isIdoksNewTheme]);

  // --------------------------------------------------------------------------
  // 7. Render
  // --------------------------------------------------------------------------

  // ── Session error screen ───────────────────────────────────────────────────
  if (sessionError && !loading && sessions.length === 0) {
    return (
      <div style={{
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        width: '100vw', height: '100vh', background: '#f5f6fa',
      }}>
        <div style={{
          textAlign: 'center', padding: '40px 32px',
          background: '#fff', borderRadius: '16px',
          boxShadow: '0 4px 24px rgba(0,0,0,0.08)',
          maxWidth: '340px', width: '90%',
        }}>
          <div style={{
            width: 64, height: 64, borderRadius: '50%',
            background: '#fff7ed', margin: '0 auto 16px',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>
            <svg width="28" height="28" viewBox="0 0 24 24" fill="none">
              <circle cx="12" cy="12" r="10" stroke="#ea580c" strokeWidth="2"/>
              <path d="M12 7v5" stroke="#ea580c" strokeWidth="2" strokeLinecap="round"/>
              <circle cx="12" cy="16" r="1" fill="#ea580c"/>
            </svg>
          </div>
          <h3 style={{ margin: '0 0 8px', fontSize: '17px', fontWeight: 700, color: '#111827' }}>
            Connection Error
          </h3>
          <p style={{ margin: '0 0 16px', fontSize: '13px', color: '#6b7280', lineHeight: 1.6 }}>
            {sessionError}
          </p>
          <button
            onClick={() => (hasRetry ? retrySessionAction() : window.location.reload())}
            style={{
              padding: '8px 20px', background: '#ea580c', color: '#fff',
              border: 'none', borderRadius: '8px', cursor: 'pointer',
              fontSize: '13px', fontWeight: 600,
            }}
          >
            {hasRetry ? 'Retry' : 'Reload'}
          </button>
        </div>
      </div>
    );
  }

  // ── Inactive agent screen ──────────────────────────────────────────────────
  if (isAgentInactive) {
    return (
      <div style={{
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        width: '100vw', height: '100vh',
        background: '#f5f6fa',
      }}>
        <div style={{
          textAlign: 'center', padding: '40px 32px',
          background: '#fff', borderRadius: '16px',
          boxShadow: '0 4px 24px rgba(0,0,0,0.08)',
          maxWidth: '340px', width: '90%',
        }}>
          <div style={{
            width: 64, height: 64, borderRadius: '50%',
            background: '#fef2f2', margin: '0 auto 16px',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>
            <svg width="28" height="28" viewBox="0 0 24 24" fill="none">
              <circle cx="12" cy="12" r="10" stroke="#dc2626" strokeWidth="2"/>
              <path d="M12 7v5" stroke="#dc2626" strokeWidth="2" strokeLinecap="round"/>
              <circle cx="12" cy="16" r="1" fill="#dc2626"/>
            </svg>
          </div>
          <h3 style={{ margin: '0 0 8px', fontSize: '17px', fontWeight: 700, color: '#111827' }}>
            Chat Unavailable
          </h3>
          <p style={{ margin: 0, fontSize: '13px', color: '#6b7280', lineHeight: 1.6 }}>
            This chat assistant has been deactivated<br />Please contact the site admin
          </p>
        </div>
      </div>
    );
  }

  return (
    <>
      {/* Inject dynamic primary color — works for any hex value at runtime */}
      <style>{botColorCSS}</style>

      {/* ------------------------------------------------------------------ */}
      {/* Floating chat bubble (shown when the chat is minimised)             */}
      {/* ------------------------------------------------------------------ */}
      {!isChatOpen && (
        <button
          id="chatwindowtoggle"
          onClick={toggleChat}
          className={`fixed text-white z-50 ${bottheme}`}
          style={{
            borderRadius: '50%',
            width: `${activeBubbleSize}px`,
            height: `${activeBubbleSize}px`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            border: 'none',
            cursor: 'pointer',
            overflow: 'hidden',
            transition: 'background-color 0.3s ease',
            // Dynamic position — use offsets for custom_agent, default edge snap for all others
            bottom: agent === 'custom_agent' ? `${activeBubbleOffsetBottom}px` : '0px',
            ...(agent === 'custom_agent'
              ? activePosition === 'bottom-left'
                ? { left: `${activeBubbleOffsetSide}px`, right: 'auto' }
                : { right: `${activeBubbleOffsetSide}px`, left: 'auto' }
              : { right: '0px', left: 'auto' }
            ),
          }}
          aria-label={isChatOpen ? 'Close chat' : 'Open chat'}
        >
          {activeBubbleImage ? (
            /* Custom bubble image uploaded by admin */
            <img
              src={activeBubbleImage}
              alt="Bot"
              style={{ width: '100%', height: '100%', borderRadius: '50%', objectFit: 'cover' }}
            />
          ) : (activeBotName === 'iKomet') ? (
            <svg xmlns="http://www.w3.org/2000/svg" width="40" height="40" viewBox="0 0 80 80">
              <g id="Group_1" data-name="Group 1" transform="translate(-672.405 -501)">
                <g id="chat-dots" transform="translate(671.155 499.75)">
                  <path id="Path_84" data-name="Path 84" d="M41.25,77.917c4.583.333,8.6-9,8.6-9l6.667-1.333,15-4.667,7.667-12.333-2.667-35-5-6.667L65.179,4.583h-44l-7,1.667L6.512,12.583l-2,9.667v31l6,8.333,6.333,6H31.512S36.667,77.583,41.25,77.917Z" fill="#fff" />
                  <path id="Path_83" data-name="Path 83" d="M45.294,76.181,47.7,77.6Zm2.017-3.408-2.4-1.421Zm-12.121,0-2.4,1.422h0Zm2.017,3.408,2.4-1.421ZM5.457,55.812l2.578-1.068h0ZM25.583,67.265l-.048,2.79ZM15.526,65.88l-1.068,2.578h0ZM77.043,55.812l2.578,1.068h0ZM56.917,67.265l-.048-2.79ZM66.974,65.88l1.068,2.578h0Zm2.6-59.1L68.118,9.162Zm6.142,6.142,2.38-1.458h0ZM12.924,6.782,11.466,4.4h0ZM6.782,12.924,4.4,11.466h0Zm24.8,55.153,1.4-2.412h0ZM47.7,77.6l2.017-3.408-4.8-2.843L42.892,74.76ZM32.788,74.195,34.805,77.6l4.8-2.843-2.017-3.408Zm10.1.565a1.938,1.938,0,0,1-3.284,0l-4.8,2.843a7.518,7.518,0,0,0,12.89,0ZM35.669,6.831H46.832V1.25H35.669Zm40,28.837V39.39H81.25V35.669ZM6.831,39.39V35.669H1.25V39.39Zm-5.581,0c0,4.3,0,7.658.183,10.37a22.162,22.162,0,0,0,1.445,7.12l5.157-2.136A16.762,16.762,0,0,1,7,49.379c-.169-2.477-.171-5.617-.171-9.99ZM25.631,64.475c-4.671-.081-7.119-.378-9.038-1.172l-2.136,5.156c3,1.241,6.407,1.516,11.078,1.6ZM2.879,56.88A21.4,21.4,0,0,0,14.458,68.459L16.594,63.3a15.814,15.814,0,0,1-8.558-8.559Zm72.79-17.49c0,4.372,0,7.512-.17,9.99a16.769,16.769,0,0,1-1.033,5.364l5.156,2.136a22.159,22.159,0,0,0,1.445-7.12c.185-2.711.183-6.073.183-10.37Zm-18.7,30.665c4.671-.08,8.083-.356,11.078-1.6L65.907,63.3c-1.919.795-4.367,1.092-9.038,1.172Zm17.5-15.311A15.815,15.815,0,0,1,65.907,63.3l2.136,5.156A21.4,21.4,0,0,0,79.622,56.88ZM46.832,6.831c6.144,0,10.556,0,14,.33,3.4.323,5.564.943,7.288,2L71.034,4.4a21.859,21.859,0,0,0-9.676-2.8c-3.767-.358-8.49-.355-14.526-.355ZM81.25,35.669c0-6.036,0-10.759-.355-14.526a21.858,21.858,0,0,0-2.8-9.676l-4.759,2.916c1.057,1.725,1.677,3.888,2,7.288.327,3.442.33,7.854.33,14ZM68.118,9.162a15.811,15.811,0,0,1,5.221,5.221L78.1,11.466A21.394,21.394,0,0,0,71.034,4.4ZM35.669,1.25c-6.036,0-10.759,0-14.526.355a21.857,21.857,0,0,0-9.676,2.8l2.916,4.759c1.725-1.057,3.888-1.677,7.288-2,3.442-.327,7.854-.33,14-.33ZM6.831,35.669c0-6.144,0-10.556.33-14,.323-3.4.943-5.563,2-7.288L4.4,11.466a21.857,21.857,0,0,0-2.8,9.676c-.358,3.767-.355,8.49-.355,14.526ZM11.466,4.4A21.4,21.4,0,0,0,4.4,11.466l4.759,2.916a15.814,15.814,0,0,1,5.221-5.221ZM37.591,71.352c-.755-1.276-1.418-2.4-2.063-3.287a8.294,8.294,0,0,0-2.539-2.4L30.183,70.49a2.842,2.842,0,0,1,.834.862c.452.62.962,1.476,1.771,2.843Zm-12.056-1.3c1.634.028,2.669.049,3.458.136a2.929,2.929,0,0,1,1.19.3l2.806-4.825a8.349,8.349,0,0,0-3.382-1.021c-1.107-.122-2.445-.143-3.976-.169Zm24.177,4.14c.809-1.367,1.319-2.223,1.771-2.843a2.841,2.841,0,0,1,.834-.862l-2.806-4.825a8.291,8.291,0,0,0-2.538,2.4c-.645.885-1.308,2.011-2.064,3.287Zm7.156-9.72c-1.53.026-2.868.047-3.976.169a8.349,8.349,0,0,0-3.382,1.021l2.806,4.825a2.932,2.932,0,0,1,1.19-.3c.789-.087,1.824-.108,3.458-.136Z" transform="translate(0 0)" fill="#fff" />
                </g>
                <g id="IKT-BRAND-Chat" transform="translate(214.413 440.45)">
                  <path id="Path_78" data-name="Path 78" d="M669.651,989.36H653.62a27.984,27.984,0,0,0,3.156,12.9h12.875v12.412a29.7,29.7,0,0,0,12.844,2.864h.053V989.36Z" transform="translate(-184.557 -894.374)" fill="#febe2d" />
                  <path id="Path_79" data-name="Path 79" d="M756.66,1038.67l4.888,13.934a21.545,21.545,0,0,1-4.888.4Z" transform="translate(-258.668 -929.841)" fill="#0a3866" />
                  <path id="Path_77" data-name="Path 77" d="M711.477,917.123c0-15.3-12.5-28.123-28.982-28.123-16.284,0-28.875,12.715-28.875,28.123v.053h15.3a13.629,13.629,0,1,1,13.628,13.844v14.318C698.9,945.321,711.477,932.62,711.477,917.123Z" transform="translate(-184.557 -822.19)" fill="#19447a" />
                  <path id="Path_80" data-name="Path 80" d="M669.651,989.36H653.62a27.984,27.984,0,0,0,3.156,12.9h12.875v12.412a29.7,29.7,0,0,0,12.844,2.864h.053V989.36Z" transform="translate(-184.557 -894.374)" fill="#febe2d" />
                  <path id="Path_82" data-name="Path 82" d="M711.477,917.123c0-15.3-12.5-28.123-28.982-28.123-16.284,0-28.875,12.715-28.875,28.123v.053h15.3a13.629,13.629,0,1,1,13.628,13.844v14.318C698.9,945.321,711.477,932.62,711.477,917.123Z" transform="translate(-184.557 -822.19)" fill="#19447a" />
                  <path id="Path_81" data-name="Path 81" d="M756.66,1038.67l4.888,13.934a21.545,21.545,0,0,1-4.888.4Z" transform="translate(-258.668 -929.841)" fill="#0a3866" />
                </g>
              </g>
            </svg>
            // <p>if</p>
          ) : (
            <svg style={{ width: '40px', height: '40px' }} color="inherit" viewBox="0 0 32 32" className="css-1mpchac">
              
              <path
                fill="#FFF"
                d="M12.63 26.46h-3.8a6.61 6.61 0 01-6.65-6.07 89.05 89.05 0 010-11.2 6.5 6.5 0 016.05-5.94 121.62 121.62 0 0115.51 0 6.51 6.51 0 016.06 5.94 77.53 77.53 0 010 11.2 6.61 6.61 0 01-6.66 6.07h-3.66L12.63 31v-4.54"
              />
              <path
                fill={activeSecondaryColor}
                d="M19.57 21.68h3.67a2.08 2.08 0 002.11-1.81 89.86 89.86 0 000-10.38 1.9 1.9 0 00-1.84-1.74 113.15 113.15 0 00-15 0 1.9 1.9 0 00-1.8 1.74 74.92 74.92 0 00-.06 10.38 2 2 0 002.1 1.81h3.81v4.82z"
                className="css-1adcsh3 eam5rsy0"
              />
              <path
                style={{ zIndex: 1 }}
                d="M10.132 14.052c-.517-.179-1.132.126-1.132.583v2.343c0 .325.217.624.58.762C10.683 18.162 13.227 19 16 19s5.317-.838 6.42-1.26c.363-.138.58-.437.58-.762v-2.343c0-.457-.614-.762-1.132-.583-1.277.441-3.489 1.059-5.868 1.059-2.379 0-4.59-.618-5.868-1.059z"
                fill="#FFF"
              />
            </svg>
            // <p>else</p>
          )}
        </button>
      )}

      {/* ------------------------------------------------------------------ */}
      {/* Main chat window — always mounted, hidden via CSS when closed.      */}
      {/* Using display:none instead of {isChatOpen && ...} so the component  */}
      {/* is never unmounted mid-request; messages and state survive close +  */}
      {/* reopen without going blank while /chat is still in-flight.          */}
      {/* ------------------------------------------------------------------ */}
      <div
          id="chatwindow"
          className={
            floatingWidget === false
              ? 'caw-window chatagent h-screen w-screen p-0 bg-white'
              : (agent === 'custom_agent'
                  ? 'caw-window fixed bg-white shadow-2xl flex flex-col z-40 overflow-hidden'
                  : 'caw-window fixed bottom-5 left-0 right-2 h-[calc(100vh-7rem)] sm:bottom-4 sm:right-3 sm:w-[500px] sm:h-[calc(100vh-7rem)] sm:left-auto bg-white ml-1 shadow-2xl rounded-lg flex flex-col z-40 overflow-hidden')
          }
          style={{
            ...(agent === 'custom_agent' && floatingWidget !== false ? chatWindowStyle : {}),
            display: isChatOpen ? undefined : 'none',
          }}
        >
          {/* Minimise button */}
          <button
            onClick={toggleChat}
            className="caw-minimise absolute top-0 right-2 text-white-500 hover:text-white-700 p-1"
            aria-label="Minimize chat"
          >
            <FiMinus style={{ width: '30px', height: '30px', color: '#fff' }} />
          </button>

          {/* ---------------------------------------------------------------- */}
          {/* Screen A: No agent selected → show agent-picker grid             */}
          {/* ---------------------------------------------------------------- */}
          {!agent && (
            <AgentSelector
              bottheme={bottheme}
              searchTerm={searchTerm}
              setSearchTerm={setSearchTerm}
              onSelectAgent={onSelectAgent}
            />
          )}

          {/* ---------------------------------------------------------------- */}
          {/* Screen B: Agent selected → show chat UI                          */}
          {/* ---------------------------------------------------------------- */}
          {agent && (
            <Chat
              viewType="chat"
              sessions={sessions}
              activeSessionId={activeSessionId || undefined}
              isLoading={isTyping}
              onStopMessage={cancelRequest}
              onNewSession={handleNewSession}
              onSelectSession={handleSelectSession}
              onSendMessage={handleSendMessage}
              theme={parsedTheme}
            >
              <SessionMessagePanel style={{ position: 'relative' }}>

                {/* Chat header bar */}
                <ChatHeader
                  agent={agent}
                  botName={activeBotName}
                  botDescription={agent === 'custom_agent' ? customAgentConfig?.description || '' : ''}
                  botImage={activeBotImage}
                  bottheme={bottheme}
                  showMenu={showMenu}
                  setShowMenu={setShowMenu}
                  onClearChat={onClearChat}
                  onBackToSelector={onBackToSelector}
                  onViewAgentMap={onViewAgentMap}
                  onExportChat={onExportChat}
                />

                {/* New-session creating overlay — shown while the backend spins up a fresh session */}
                {isCreatingSession && (
                  <div style={{
                    position: 'absolute', inset: 0, zIndex: 20,
                    display: 'flex', flexDirection: 'column',
                    alignItems: 'center', justifyContent: 'center',
                    background: 'rgba(255,255,255,0.82)',
                    backdropFilter: 'blur(3px)',
                    gap: '14px',
                  }}>
                    {/* Spinner */}
                    <div style={{
                      width: '36px', height: '36px',
                      borderRadius: '50%',
                      border: '3px solid #e5e7eb',
                      borderTopColor: bottheme?.primary || '#4B6382',
                      animation: 'spin 0.75s linear infinite',
                    }} />
                    <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
                    <div style={{ fontSize: '13px', fontWeight: 600, color: '#374151' }}>
                      Starting new conversation…
                    </div>
                    <div style={{ fontSize: '12px', color: '#9ca3af' }}>
                      Setting up your session
                    </div>
                  </div>
                )}

                {/* Messages list */}
                <div
                  className="caw-messages flex-grow h-full overflow-y-auto overflow-x-hidden p-4 custom-scrollbar"
                  ref={messagesContainerRef}
                  onScroll={() => { stickToBottomRef.current = isNearBottom(); }}
                >
                  {currentSessionMessages.map((message, idx) => (
                    <div key={message.id || idx} ref={idx === currentSessionMessages.length - 1 ? lastMsgRef : null}>
                      <CustomChatMessage
                        message={message}
                        bottheme={bottheme}
                        botImage={activeBotImage}
                        isTyping={isTyping && message.sender === 'bot' && message.id === 'typing-indicator'}
                        onSend={handleSendMessage}
                        onRetry={retrySessionAction}
                        timezone={userTimezone}
                        onViewFullTrace={() => onViewFullTrace(message, currentSessionMessages[idx - 1]?.content)}
                        onTypewriterTick={keepNewestTextInView}
                        tookMs={answerTookMs(message, currentSessionMessages[idx - 1])}
                      />
                    </div>
                  ))}

                  {/* Typing indicator */}
                  {isTyping && (
                    <CustomChatMessage
                      message={mapBackendMessagesToReachat({
                        id: 'typing-indicator',
                        sender: 'bot',
                        content: { kind: 'typing' },
                      })[0]}
                      bottheme={bottheme}
                      botImage={activeBotImage}
                      isTyping={true}
                      timezone={userTimezone}
                      progress={progress}
                    />
                  )}

                  {/* Invisible anchor — scrollIntoView target for auto-scroll to bottom */}
                  <div ref={bottomAnchorRef} style={{ height: 1 }} />
                </div>

                {/* Chat input area */}
                <div className="caw-footer p-0 relative border-gray-300">

                  {/* Selected-files badge (shown for file-based agents) */}
                  {agents[agent]?.isbot !== true && pickedFiles.length > 0 && (
                    <div className={`caw-scope-bar p-2 pl-4 mb-1 ${bottheme} text-white border-top`}>
                      <div className="d-flex flex-wrap gap-2">
                        <p className="mb-1">
                          <span className="font-medium"> You've selected these files:</span>
                          {pickedFiles.map((file, idx) => (
                            <span
                              key={idx}
                              className="badge bg-primary text-white px-3 py-2"
                              style={{ fontSize: '0.9rem', borderRadius: '1rem' }}
                            >
                              📎 {file.name}
                            </span>
                          ))}
                        </p>
                      </div>
                    </div>
                  )}

                  {/* Section/directory filter badge (idokschat_agent only) */}
                  {defaultbot === 'idokschat_agent' && (
                    <div className={`caw-scope-bar p-2 pl-4 mb-1 ${bottheme} text-white border-top`}>
                      <div className="d-flex flex-wrap gap-2">
                        {pickedSections !== '' ? (
                          <p className="mb-1 flex items-center gap-2">
                            <span className="text-sm">
                              Your search will be limited to the <b>{pickedSections}</b> folder/section.
                            </span>
                            <button
                              onClick={handleClear}
                              className="caw-scope-clear flex items-center text-xs text-gray-600 hover:text-red-600 ml-2"
                              title="Clear"
                            >
                              ❌
                            </button>
                          </p>
                        ) : (
                          <p className="mb-1">
                            <span className="font-small">Your search will include all available files.</span>
                          </p>
                        )}
                      </div>
                    </div>
                  )}

                  {/* ── custom_agent: Quick-reply suggestion chips ─────────────── */}
                  {/* Shown above the input box, hidden once the user sends a message */}
                  {agent === 'custom_agent' &&
                    customAgentConfig?.suggestions?.length > 0 &&
                    !currentSessionMessages.some(m => m.sender === 'user') && (
                    <div className="caw-quick" style={{
                      padding: '6px 12px 8px',
                      display: 'flex',
                      flexWrap: 'wrap',
                      gap: '8px',
                      borderTop: '1px solid #f0f0f0',
                    }}>
                      {customAgentConfig.suggestions.map((s, idx) => (
                        <button
                          key={idx}
                          onClick={() => handleSendMessage(s.query)}
                          className="bot-primary-color caw-quick-chip"
                          style={{
                            padding: '5px 14px',
                            borderRadius: '999px',
                            border: 'none',
                            color: '#fff',
                            fontSize: '13px',
                            cursor: 'pointer',
                            whiteSpace: 'nowrap',
                          }}
                        >
                          {s.query}
                        </button>
                      ))}
                    </div>
                  )}

                  <div
                    className={agents[agent]['sourcerequired'] === true && pickedFiles.length === 0 ? 'disabled p-2 pt-0' : 'p-2 pt-0'}
                  >
                    <div className="caw-inputbar" ref={chatInputRef}>
                      <ChatInput onSend={handleSendMessage} />
                    </div>
                  </div>

                  {/* ── custom_agent: "Powered by iDoks" branding badge ────────── */}
                  {activeShowBranding && (
                    <div className="caw-branding" style={{
                      textAlign: 'center',
                      fontSize: '11px',
                      color: '#9ca3af',
                      padding: '2px 0 6px',
                      letterSpacing: '0.01em',
                    }}>
                      Powered by{' '}
                      <a
                        href="https://idoks.in"
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{ color: '#6b7280', fontWeight: 600, textDecoration: 'none' }}
                      >
                        iDoks
                      </a>
                    </div>
                  )}

                </div>
              </SessionMessagePanel>
            </Chat>
          )}

        </div>
    </>
  );
};

export default ChatAgent;
